import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowRight, ArrowLeft, ShieldCheck, FileCheck2, Upload, X, Check,
    Building2, User, MapPin, Briefcase, Landmark, ScrollText, Loader2,
} from "lucide-react";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";
import { PartnerProgrammeLanding, FreePartnerRegistration, PAID_PENDING_MESSAGE } from "@/components/PartnerProgramme";

const BUSINESS_TYPES = [
    { v: "individual_broker",     l: "Individual Broker" },
    { v: "real_estate_consultant", l: "Real Estate Consultant" },
    { v: "proprietorship",        l: "Proprietorship" },
    { v: "partnership_firm",      l: "Partnership Firm" },
    { v: "llp",                   l: "LLP" },
    { v: "private_limited",       l: "Private Limited Company" },
    { v: "other",                 l: "Other" },
];
const EXPERIENCE = [
    { v: "less_1",   l: "Less than 1 Year" },
    { v: "1_to_3",   l: "1 – 3 Years" },
    { v: "3_to_5",   l: "3 – 5 Years" },
    { v: "5_to_10",  l: "5 – 10 Years" },
    { v: "more_10",  l: "More than 10 Years" },
];
const AREAS = [
    "Chandigarh", "Mohali", "Panchkula", "Zirakpur", "New Chandigarh",
    "Kharar", "Derabassi", "Punjab", "Haryana", "Delhi NCR",
];
const CATEGORIES = [
    "Residential", "Commercial", "Industrial", "Agricultural Land", "Farm House",
    "Plots", "Builder Floors", "Villas", "Luxury Homes", "SCO / Booth",
];
const SERVICES = [
    "Property Sale", "Property Purchase", "Rental", "Leasing",
    "Investment Advisory", "Builder Projects", "Resale Properties",
];

const DOC_SLOTS = [
    { k: "passport_photo",       l: "Passport-size Photograph",  required: true,  accept: "image/*" },
    { k: "pan_card",             l: "PAN Card",                   required: true,  accept: "image/*,application/pdf" },
    { k: "aadhaar_card",         l: "Aadhaar Card",               required: true,  accept: "image/*,application/pdf" },
    { k: "cancelled_cheque",     l: "Cancelled Cheque",           required: true,  accept: "image/*,application/pdf" },
    { k: "visiting_card",        l: "Visiting Card",              required: true,  accept: "image/*,application/pdf" },
    { k: "selfie",               l: "Selfie with Aadhaar / PAN (KYCP)", required: true, accept: "image/*" },
    { k: "gst_certificate",      l: "GST Certificate (if any)",   required: false, accept: "image/*,application/pdf" },
    { k: "rera_certificate",     l: "RERA Certificate (if any)",  required: false, accept: "image/*,application/pdf" },
    { k: "company_registration", l: "Company Registration Certificate (if any)", required: false, accept: "image/*,application/pdf" },
];

const emptyForm = {
    company_name: "", proprietor_name: "", mobile: "", alternate_mobile: "",
    email: "", office_address: "", city: "", state: "", pin_code: "",
    business_type: "", business_type_other: "",
    rera_registration_number: "", gst_number: "", pan_number: "",
    experience_bucket: "",
    areas_of_operation: [], areas_other: "",
    property_categories: [], categories_other: "",
    services_offered: [], services_other: "",
    banking: { account_holder_name: "", bank_name: "", account_number: "", ifsc_code: "", upi_id: "" },
    declaration_accepted: false, terms_accepted: false,
    signature_name: "", signed_date: new Date().toISOString().slice(0, 10), signed_place: "",
};

const STEPS = [
    { k: "partner",  l: "Channel Partner",  Icon: User },
    { k: "business", l: "Business Info",    Icon: Briefcase },
    { k: "banking",  l: "Banking",          Icon: Landmark },
    { k: "docs",     l: "Documents",        Icon: FileCheck2 },
    { k: "review",   l: "Declaration",      Icon: ScrollText },
];


export default function ChannelPartnerRegistration() {
    const [params] = useSearchParams();
    const type = params.get("type");
    const [freeDone, setFreeDone] = useState(null);
    if (type === "premium" || type === "paid") return <PaidRegistrationWizard/>;
    if (freeDone) return <SuccessScreen type="FREE" id={freeDone.id} email={freeDone.email || freeDone.mobile}/>;
    return (
        <>
            <Nav variant="light"/>
            <main className="min-h-screen bg-[var(--ebg-ivory)] pt-28 pb-24">
                <div className="max-w-6xl mx-auto px-4 md:px-6">
                    {type === "partner" || type === "free" ? <FreePartnerRegistration onDone={setFreeDone}/> : <PartnerProgrammeLanding/>}
                </div>
            </main>
            <Footer/>
        </>
    );
}

function SuccessScreen({ type, id, email }) {
    const paid = type === "PAID";
    useEffect(() => { window.scrollTo(0, 0); }, []);
    return (
        <>
            <Nav variant="light"/>
            <main className="min-h-screen bg-[var(--ebg-ivory)] pt-32 pb-24">
                <div className="max-w-2xl mx-auto px-6" data-testid="cp-registration-success">
                    <div className="bg-white border border-black/8 p-10 md:p-14 text-center">
                        <div className="w-16 h-16 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 flex items-center justify-center mx-auto">
                            <Check className="text-[var(--ebg-gold)]" size={30}/>
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)] mt-6" data-testid="cp-success-status">
                            {paid ? "Registration Submitted — Pending EBG Review" : "Channel Partner Registration Received"}
                        </div>
                        <h1 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">{paid ? <>Thank you for<br/>registering.</> : <>Welcome to the<br/>EBG Realty family.</>}</h1>
                        <p className="text-black/60 mt-5 leading-relaxed" data-testid="cp-success-message">
                            {paid ? PAID_PENDING_MESSAGE : <>Our partner-relations team will review your registration and get back to you within 2 business days at <span className="font-bold text-black">{email}</span>.</>}
                        </p>
                        <p className="text-black/50 text-sm mt-6">
                            Application ID <span className="font-bold text-black">{id.slice(0, 8).toUpperCase()}</span>. Once approved, you&apos;ll receive your login details to access your Channel Partner portal.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-3 justify-center">
                            <Link to="/" className="border border-black text-black px-8 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-white">Back to Home</Link>
                            <Link to="/properties" className="bg-[var(--ebg-gold)] text-black px-8 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-white">Browse Portfolio</Link>
                        </div>
                    </div>
                </div>
            </main>
            <Footer/>
        </>
    );
}

function PaidRegistrationWizard() {
    const [step, setStep] = useState(0);
    const [f, setF] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null); // { id, status }
    const [uploading, setUploading] = useState({});   // slot -> bool
    const [uploaded, setUploaded]  = useState({});    // slot -> { filename, size }
    const nav = useNavigate();

    const set = (patch) => setF((p) => ({ ...p, ...patch }));
    const setBank = (patch) => setF((p) => ({ ...p, banking: { ...p.banking, ...patch } }));
    const toggle = (key, val) => setF((p) => ({
        ...p, [key]: p[key].includes(val) ? p[key].filter((v) => v !== val) : [...p[key], val],
    }));

    const canNext = () => {
        if (step === 0) return f.company_name && f.proprietor_name && f.mobile && f.email && f.office_address && f.city && f.state && f.pin_code;
        if (step === 1) return f.business_type && f.pan_number && f.experience_bucket && f.areas_of_operation.length && f.property_categories.length && f.services_offered.length;
        if (step === 2) return f.banking.account_holder_name && f.banking.bank_name && f.banking.account_number && f.banking.ifsc_code;
        if (step === 3) return DOC_SLOTS.filter((s) => s.required).every((s) => uploaded[s.k]);
        return true;
    };

    const submitApplication = async () => {
        if (!f.declaration_accepted || !f.terms_accepted) {
            toast.error("Please accept the declaration and the terms & conditions.");
            return;
        }
        setSubmitting(true);
        try {
            const areas = f.areas_of_operation.slice();
            if (f.areas_other) areas.push(f.areas_other);
            const cats = f.property_categories.slice();
            if (f.categories_other) cats.push(f.categories_other);
            const svcs = f.services_offered.slice();
            if (f.services_other) svcs.push(f.services_other);

            const payload = {
                company_name: f.company_name, proprietor_name: f.proprietor_name,
                mobile: f.mobile, alternate_mobile: f.alternate_mobile || null,
                email: f.email, office_address: f.office_address,
                city: f.city, state: f.state, pin_code: f.pin_code,
                business_type: f.business_type,
                business_type_other: f.business_type === "other" ? f.business_type_other : null,
                rera_registration_number: f.rera_registration_number || null,
                gst_number: f.gst_number || null,
                pan_number: f.pan_number,
                experience_bucket: f.experience_bucket,
                areas_of_operation: areas, property_categories: cats, services_offered: svcs,
                banking: f.banking,
                declaration_accepted: f.declaration_accepted,
                terms_accepted: f.terms_accepted,
                signature_name: f.signature_name,
                signed_date: f.signed_date,
                signed_place: f.signed_place,
            };
            const { data } = await api.post("/cp-applications", payload);

            // Upload any pending documents (they were queued locally with app_id=null)
            const uploads = [];
            for (const slot of Object.keys(uploaded)) {
                const info = uploaded[slot];
                if (info?._file) uploads.push(uploadFileFor(data.id, slot, info._file));
            }
            await Promise.all(uploads);

            setSubmitted(data);
            toast.success("Application submitted — we'll be in touch within 2 business days.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    const uploadFileFor = async (appId, slot, file) => {
        setUploading((p) => ({ ...p, [slot]: true }));
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/cp-applications/${appId}/upload?slot=${slot}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setUploaded((p) => ({ ...p, [slot]: { ...data, _file: null } }));
        } catch (e) {
            toast.error(`Upload failed (${slot}): ${formatApiError(e)}`);
            setUploaded((p) => { const n = { ...p }; delete n[slot]; return n; });
        } finally {
            setUploading((p) => ({ ...p, [slot]: false }));
        }
    };

    const stageFile = (slot, file) => {
        // Stage in-memory until we have an app_id from submit; then batch-upload
        if (file.size > 5 * 1024 * 1024) {
            toast.error("File too large — 5 MB max.");
            return;
        }
        setUploaded((p) => ({ ...p, [slot]: { filename: file.name, size: file.size, _file: file } }));
    };

    // ============================== SUCCESS SCREEN ==============================
    if (submitted) return <SuccessScreen type="PAID" id={submitted.id} email={f.email}/>;
    // ============================== MAIN FORM ==============================
    return (
        <>
            <Nav variant="light"/>
            <main className="min-h-screen bg-[var(--ebg-ivory)] pt-28 pb-24">
                <div className="max-w-5xl mx-auto px-4 md:px-6" data-testid="cp-registration-page">
                    {/* Header */}
                    <header className="text-center mb-10 md:mb-14">
                        <div className="text-[10px] md:text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]" data-testid="cp-paid-eyebrow">Premium Channel Partner Registration</div>
                        <h1 className="font-serif text-4xl md:text-6xl mt-3 leading-[1.05]">Join India&apos;s most trusted<br/><em className="not-italic text-[var(--ebg-gold)]">real-estate brokerage.</em></h1>
                        <p className="text-black/60 mt-5 max-w-2xl mx-auto text-sm md:text-base">
                            Complete the registration below to become an EBG Realty Channel Partner. All applications are manually reviewed
                            by our partner-relations team within 2 business days.
                        </p>
                        <p className="mt-4 inline-block text-[12px] text-black/70 bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 px-4 py-2" data-testid="cp-paid-no-payment-note">
                            No payment is collected online. After review, our team will contact you regarding eligibility, payment and membership confirmation.
                        </p>
                        <div className="mt-4"><Link to="/partner-registration" data-testid="cp-paid-back" className="text-[11px] uppercase tracking-[0.22em] font-bold text-black/50 hover:text-black">&larr; Change partner type</Link></div>
                    </header>

                    {/* Stepper */}
                    <div className="flex items-center justify-between mb-10 overflow-x-auto no-scrollbar" data-testid="cp-stepper">
                        {STEPS.map((s, i) => (
                            <div key={s.k} className={`flex items-center gap-3 shrink-0 ${i > 0 ? "flex-1 min-w-0 pl-2" : ""}`}>
                                {i > 0 && <div className={`h-px flex-1 hidden md:block ${i <= step ? "bg-[var(--ebg-gold)]" : "bg-black/15"}`}/>}
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${i === step ? "bg-[var(--ebg-gold)] text-black" : i < step ? "bg-black text-white" : "bg-white border border-black/15 text-black/40"}`}>
                                    {i < step ? <Check size={14}/> : <s.Icon size={14}/>}
                                </div>
                                <div className={`text-[10px] md:text-[11px] uppercase tracking-[0.18em] font-bold whitespace-nowrap ${i === step ? "text-black" : "text-black/50"}`}>{s.l}</div>
                            </div>
                        ))}
                    </div>

                    <div className="bg-white border border-black/8 p-6 md:p-10">
                        {/* STEP 0 — Partner details */}
                        {step === 0 && (
                            <div className="grid md:grid-cols-2 gap-5" data-testid="step-partner">
                                <Field l="Company / Firm Name*" testid="cp-company">
                                    <Input value={f.company_name} onChange={(v) => set({ company_name: v })} />
                                </Field>
                                <Field l="Name of Proprietor / Authorized Person*" testid="cp-proprietor">
                                    <Input value={f.proprietor_name} onChange={(v) => set({ proprietor_name: v })} />
                                </Field>
                                <Field l="Mobile Number*" testid="cp-mobile">
                                    <Input value={f.mobile} onChange={(v) => set({ mobile: v })} type="tel"/>
                                </Field>
                                <Field l="Alternate Mobile Number">
                                    <Input value={f.alternate_mobile} onChange={(v) => set({ alternate_mobile: v })} type="tel"/>
                                </Field>
                                <Field l="Email Address*" testid="cp-email">
                                    <Input value={f.email} onChange={(v) => set({ email: v })} type="email"/>
                                </Field>
                                <Field l="Office Address*" full testid="cp-office" >
                                    <textarea rows="2" value={f.office_address} onChange={(e) => set({ office_address: e.target.value })} className={inputCls}/>
                                </Field>
                                <Field l="City*" testid="cp-city"><Input value={f.city} onChange={(v) => set({ city: v })}/></Field>
                                <Field l="State*" testid="cp-state"><Input value={f.state} onChange={(v) => set({ state: v })}/></Field>
                                <Field l="PIN Code*" testid="cp-pin"><Input value={f.pin_code} onChange={(v) => set({ pin_code: v })}/></Field>
                            </div>
                        )}

                        {/* STEP 1 — Business info */}
                        {step === 1 && (
                            <div className="space-y-6" data-testid="step-business">
                                <div className="grid md:grid-cols-2 gap-5">
                                    <Field l="Business Type*" testid="cp-btype">
                                        <select value={f.business_type} onChange={(e) => set({ business_type: e.target.value })} className={inputCls}>
                                            <option value="">Select…</option>
                                            {BUSINESS_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                                        </select>
                                    </Field>
                                    {f.business_type === "other" && (
                                        <Field l="Other Business Type*"><Input value={f.business_type_other} onChange={(v) => set({ business_type_other: v })}/></Field>
                                    )}
                                    <Field l="PAN Number*" testid="cp-pan"><Input value={f.pan_number} onChange={(v) => set({ pan_number: v.toUpperCase() })}/></Field>
                                    <Field l="Years of Experience*" testid="cp-experience">
                                        <select value={f.experience_bucket} onChange={(e) => set({ experience_bucket: e.target.value })} className={inputCls}>
                                            <option value="">Select…</option>
                                            {EXPERIENCE.map((e) => <option key={e.v} value={e.v}>{e.l}</option>)}
                                        </select>
                                    </Field>
                                    <Field l="RERA Registration Number"><Input value={f.rera_registration_number} onChange={(v) => set({ rera_registration_number: v })}/></Field>
                                    <Field l="GST Number"><Input value={f.gst_number} onChange={(v) => set({ gst_number: v })}/></Field>
                                </div>

                                <MultiSelect label="Areas of Operation*" options={AREAS} values={f.areas_of_operation}
                                    onToggle={(v) => toggle("areas_of_operation", v)} otherValue={f.areas_other}
                                    onOther={(v) => set({ areas_other: v })} testid="cp-areas"/>
                                <MultiSelect label="Property Categories*" options={CATEGORIES} values={f.property_categories}
                                    onToggle={(v) => toggle("property_categories", v)} otherValue={f.categories_other}
                                    onOther={(v) => set({ categories_other: v })} testid="cp-categories"/>
                                <MultiSelect label="Services Offered*" options={SERVICES} values={f.services_offered}
                                    onToggle={(v) => toggle("services_offered", v)} otherValue={f.services_other}
                                    onOther={(v) => set({ services_other: v })} testid="cp-services"/>
                            </div>
                        )}

                        {/* STEP 2 — Banking */}
                        {step === 2 && (
                            <div className="grid md:grid-cols-2 gap-5" data-testid="step-banking">
                                <Field l="Account Holder Name*" testid="cp-bank-holder"><Input value={f.banking.account_holder_name} onChange={(v) => setBank({ account_holder_name: v })}/></Field>
                                <Field l="Bank Name*" testid="cp-bank-name"><Input value={f.banking.bank_name} onChange={(v) => setBank({ bank_name: v })}/></Field>
                                <Field l="Account Number*" testid="cp-bank-num"><Input value={f.banking.account_number} onChange={(v) => setBank({ account_number: v })}/></Field>
                                <Field l="IFSC Code*" testid="cp-bank-ifsc"><Input value={f.banking.ifsc_code} onChange={(v) => setBank({ ifsc_code: v.toUpperCase() })}/></Field>
                                <Field l="UPI ID"><Input value={f.banking.upi_id} onChange={(v) => setBank({ upi_id: v })}/></Field>
                            </div>
                        )}

                        {/* STEP 3 — Documents */}
                        {step === 3 && (
                            <div className="grid md:grid-cols-2 gap-4" data-testid="step-docs">
                                {DOC_SLOTS.map((s) => {
                                    const u = uploaded[s.k];
                                    return (
                                        <label key={s.k} data-testid={`cp-doc-${s.k}`} className={`flex items-center gap-4 p-4 border transition-colors cursor-pointer ${u ? "border-[var(--ebg-gold)] bg-[var(--ebg-gold)]/5" : "border-black/15 hover:border-black"}`}>
                                            <div className="w-11 h-11 rounded-full bg-white border border-black/10 flex items-center justify-center shrink-0">
                                                {uploading[s.k] ? <Loader2 className="animate-spin text-black/40" size={16}/> : u ? <Check className="text-[var(--ebg-gold)]" size={16}/> : <Upload className="text-black/40" size={16}/>}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[11px] uppercase tracking-[0.18em] font-bold flex items-center gap-2">{s.l}{!s.required && <span className="text-black/40 lowercase font-normal">— optional</span>}</div>
                                                <div className="text-[11px] text-black/50 mt-1 truncate">
                                                    {u ? `${u.filename} · ${Math.ceil(u.size / 1024)} KB` : "JPG · PNG · PDF (max 5 MB)"}
                                                </div>
                                            </div>
                                            <input type="file" accept={s.accept} className="hidden"
                                                onChange={(e) => e.target.files?.[0] && stageFile(s.k, e.target.files[0])}/>
                                            {u && (
                                                <button type="button" onClick={(e) => { e.preventDefault(); setUploaded((p) => { const n = { ...p }; delete n[s.k]; return n; }); }} className="shrink-0 text-black/40 hover:text-red-500 p-1" aria-label="Remove">
                                                    <X size={14}/>
                                                </button>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        )}

                        {/* STEP 4 — Review + declaration */}
                        {step === 4 && (
                            <div className="space-y-6" data-testid="step-review">
                                <div className="bg-[var(--ebg-ivory)] border border-black/8 p-5">
                                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/60 mb-3">Declaration</div>
                                    <p className="text-[13px] leading-relaxed text-black/75">
                                        I hereby declare that all information provided by me is true and correct to the best of my knowledge. I agree
                                        to comply with the policies, terms and conditions of EBG Realty. I understand that EBG Realty is a
                                        legal-verified real estate brokerage platform and reserves the right to approve, suspend or terminate my
                                        registration if any information is found to be false or misleading. I further agree to maintain professional
                                        ethics, transparency and honesty while dealing with clients introduced through EBG Realty.
                                    </p>
                                </div>
                                <label className="flex items-start gap-3 cursor-pointer" data-testid="cp-declaration">
                                    <input type="checkbox" checked={f.declaration_accepted} onChange={(e) => set({ declaration_accepted: e.target.checked })} className="mt-1 w-4 h-4 accent-[var(--ebg-gold)]"/>
                                    <span className="text-[13px] text-black/80">I accept the declaration above.</span>
                                </label>
                                <label className="flex items-start gap-3 cursor-pointer" data-testid="cp-terms">
                                    <input type="checkbox" checked={f.terms_accepted} onChange={(e) => set({ terms_accepted: e.target.checked })} className="mt-1 w-4 h-4 accent-[var(--ebg-gold)]"/>
                                    <span className="text-[13px] text-black/80">I have read and agree to the <Link to="/legal-check" className="text-[var(--ebg-gold)] underline">Terms &amp; Conditions</Link> and Privacy Policy of EBG Realty.</span>
                                </label>
                                <div className="grid md:grid-cols-3 gap-4">
                                    <Field l="Signature (typed full name)*" testid="cp-signature"><Input value={f.signature_name} onChange={(v) => set({ signature_name: v })}/></Field>
                                    <Field l="Date*"><Input type="date" value={f.signed_date} onChange={(v) => set({ signed_date: v })}/></Field>
                                    <Field l="Place*"><Input value={f.signed_place} onChange={(v) => set({ signed_place: v })}/></Field>
                                </div>
                            </div>
                        )}

                        {/* NAV BUTTONS */}
                        <div className="mt-8 pt-6 border-t border-black/8 flex items-center justify-between">
                            <button
                                type="button" disabled={step === 0}
                                onClick={() => setStep(Math.max(0, step - 1))}
                                data-testid="cp-back"
                                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-black/60 hover:text-black disabled:opacity-30"
                            >
                                <ArrowLeft size={14}/> Back
                            </button>
                            {step < STEPS.length - 1 ? (
                                <button
                                    type="button" disabled={!canNext()}
                                    onClick={() => setStep(step + 1)}
                                    data-testid="cp-next"
                                    className="inline-flex items-center gap-2 bg-black text-white px-6 md:px-8 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Continue <ArrowRight size={14}/>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={submitApplication}
                                    disabled={submitting || !f.declaration_accepted || !f.terms_accepted || !f.signature_name || !f.signed_place}
                                    data-testid="cp-submit"
                                    className="inline-flex items-center gap-2 bg-[var(--ebg-gold)] text-black px-8 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {submitting ? <><Loader2 size={14} className="animate-spin"/> Submitting…</> : <>Submit Application <ArrowRight size={14}/></>}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Trust reassurance */}
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-black/50">
                        <span className="flex items-center gap-2"><ShieldCheck size={12} className="text-[var(--ebg-gold)]"/> Manually Reviewed</span>
                        <span className="flex items-center gap-2"><FileCheck2 size={12} className="text-[var(--ebg-gold)]"/> Data Never Sold</span>
                        <span className="flex items-center gap-2"><ScrollText size={12} className="text-[var(--ebg-gold)]"/> Digital CP Agreement</span>
                    </div>
                </div>
            </main>
            <Footer/>
        </>
    );
}

// -------------- Field primitives --------------
const inputCls = "w-full bg-white border border-black/15 px-4 py-3 text-[14px] focus:border-[var(--ebg-gold)] focus:outline-none";

function Field({ l, children, testid, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-black/55 mb-2">{l}</div>
            {children}
        </div>
    );
}
function Input({ value, onChange, type = "text" }) {
    return <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}/>;
}
function MultiSelect({ label, options, values, onToggle, otherValue, onOther, testid }) {
    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-black/55 mb-3">{label}</div>
            <div className="flex flex-wrap gap-2">
                {options.map((o) => (
                    <button type="button" key={o} onClick={() => onToggle(o)}
                        data-testid={`${testid}-${o.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] font-bold border transition-colors ${values.includes(o) ? "bg-black text-white border-black" : "border-black/15 hover:border-black"}`}
                    >{o}</button>
                ))}
                <input value={otherValue || ""} onChange={(e) => onOther(e.target.value)} placeholder="Other…" className="px-3 py-1.5 text-[11px] border border-black/15 focus:border-black outline-none w-32"/>
            </div>
        </div>
    );
}
